import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

type Period = 'day' | 'month' | 'all';

interface UsageRow {
  model: string;
  task_type: string;
  calls: number;
  errors: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
}

interface UsageData {
  period: string;
  summary: {
    total_calls: number;
    total_errors: number;
    total_tokens_in: number;
    total_tokens_out: number;
    total_cost_usd: number;
  };
  rows: UsageRow[];
}

const PERIOD_LABELS: Record<Period, string> = { day: 'Last 24h', month: 'Last 30d', all: 'All time' };

const AdminAIUsage: React.FC = () => {
  const [period, setPeriod] = useState<Period>('month');
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Admin | AI Usage';
    setLoading(true);
    api.get<UsageData>(API_ENDPOINTS.admin.aiUsage(period))
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'AI Usage' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Overview</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Plans</NavLink>
        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Blog</NavLink>
      </nav>

      <div className="admin-period-tabs">
        {(['day', 'month', 'all'] as Period[]).map(p => (
          <button
            key={p}
            className={`admin-period-tab ${period === p ? 'admin-period-tab--active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>

      {loading && <div className="admin-loading">Loading…</div>}

      {!loading && data && (
        <>
          <div className="admin-stats">
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Total Calls</div>
              <div className="admin-stat-card__value">{data.summary.total_calls}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Errors</div>
              <div className="admin-stat-card__value" style={{ color: data.summary.total_errors > 0 ? '#dc2626' : undefined }}>
                {data.summary.total_errors}
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Tokens In</div>
              <div className="admin-stat-card__value">{data.summary.total_tokens_in.toLocaleString()}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Tokens Out</div>
              <div className="admin-stat-card__value">{data.summary.total_tokens_out.toLocaleString()}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Total Cost</div>
              <div className="admin-stat-card__value">${data.summary.total_cost_usd.toFixed(4)}</div>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Task type</th>
                  <th>Calls</th>
                  <th>Errors</th>
                  <th>Tokens in</th>
                  <th>Tokens out</th>
                  <th>Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} className="admin-empty">No data for this period</td></tr>
                )}
                {data.rows.map((row, i) => (
                  <tr key={i}>
                    <td><span className="admin-badge admin-badge--blue">{row.model || '—'}</span></td>
                    <td>{row.task_type || '—'}</td>
                    <td>{row.calls}</td>
                    <td>
                      {row.errors > 0
                        ? <span className="admin-badge admin-badge--red">{row.errors}</span>
                        : <span className="admin-badge admin-badge--green">0</span>}
                    </td>
                    <td>{row.tokens_in.toLocaleString()}</td>
                    <td>{row.tokens_out.toLocaleString()}</td>
                    <td>${row.cost_usd.toFixed(6)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default AdminAIUsage;
